import { useMemo } from 'react'

import type { ActivationGroup } from '@/features/activationGroup/types'
import type { UseBidOfferCapacityBidsResult } from '@/features/bidding/hooks/useBidOfferCapacityBids'
import type {
  BidMetaData,
  BidVolumes,
  ExtendedBid,
  OfferedAndForecastAndSuggestedPtu,
} from '@/features/bidding/types/bid'
import type { BidHistory } from '@/features/bidding/types/bidHistory'
import { combineOfferedAndForecast } from '@/features/bidding/utils/calculations/calculateBasisForNewBid'
import getBidMetaData from '@/features/bidding/utils/getBidMetaData'
import { generateInitialBidPtus } from '@/features/bidding/utils/getInitialPtus'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'

/**
 * Data structure containing all the enriched PTU information for an activation group.
 * This includes the bidMetaData, the combined offered/forecast/suggested PTUs,
 * and the initial PTUs with capacity bid prefilled.
 */
export type EnrichedPtuData = {
  bidMetaData: BidMetaData
  ptus: OfferedAndForecastAndSuggestedPtu[]
  initialPtus: OfferedAndForecastAndSuggestedPtu[]
}

/** Stable default so callers that do not pass accepted bids keep a referentially stable input */
const NO_ACCEPTED_BIDS: Map<string, ExtendedBid | null> = new Map()

export type UseBidOfferEnrichedPtusResult = {
  enrichedPtusByActivationGroup: Map<string, EnrichedPtuData>
}

/**
 * Hook to generate enriched PTU data for all activation groups.
 * This hook combines data from multiple sources:
 * - bidBasis (PTUs, forecast, suggested bids)
 * - bid histories (for prefilling draft values - contains all bid types)
 * - capacity bids (for prefilling when no draft exists - mFRR Finland ENERGY only)
 * - accepted bid schedules (for expired PTUs)
 * - latest non-draft bids (fallback for expired PTUs)
 *
 * For each activation group, it:
 * 1. Gets the bidBasis data
 * 2. Creates bidMetaData
 * 3. Combines offered volumes with forecast and suggested bids
 * 4. Generates initial PTUs with appropriate prefill logic:
 *    - All markets: Drafts take priority
 *    - mFRR Finland ENERGY only: Falls back to capacity bid if no draft
 *    - Finally: Uses basis values
 *
 * @param params.groupParams - The grouped bid parameters (deliveryDay, marketProgram, portfolioCode, bidType)
 * @param params.eligibleActivationGroups - Array of activation groups to process
 * @param params.bidBasisByActivationGroup - Map of bidBasis by activationGroupUuid (from useBidOfferBidBasis)
 * @param params.latestNonDraftBidsByActivationGroup - Map of latest non-draft bids (from useBidOfferLatestNonDraftBids)
 * @param params.capacityBids - Capacity bid utilities (from useBidOfferCapacityBids)
 * @param params.bidHistories - Array of bid histories (from useBidOfferBidHistories), includes draft and offered bids for all bid types
 * @param params.acceptedBidsByActivationGroup - Map of latest accepted bid schedules (from useBidOfferAcceptedBids), used for expired PTUs
 * @returns Map of enriched PTU data by activationGroupUuid
 *
 * @example
 * ```tsx
 * const { enrichedPtusByActivationGroup } = useBidOfferEnrichedPtus({
 *   groupParams,
 *   eligibleActivationGroups: activationGroups,
 *   bidBasisByActivationGroup,
 *   latestNonDraftBidsByActivationGroup,
 *   capacityBids,
 *   bidHistories,
 * })
 *
 * const enrichedData = enrichedPtusByActivationGroup.get(activationGroupUuid)
 * ```
 */
export type UseBidOfferEnrichedPtusParams = {
  groupParams: GroupedBidParams
  eligibleActivationGroups: ActivationGroup[]
  bidBasisByActivationGroup: Map<string, BidVolumes>
  latestNonDraftBidsByActivationGroup: Map<string, ExtendedBid | null>
  capacityBids: UseBidOfferCapacityBidsResult
  bidHistories: BidHistory[]
  acceptedBidsByActivationGroup?: Map<string, ExtendedBid | null>
}

export function useBidOfferEnrichedPtus({
  groupParams,
  eligibleActivationGroups,
  bidBasisByActivationGroup,
  latestNonDraftBidsByActivationGroup,
  capacityBids,
  bidHistories,
  acceptedBidsByActivationGroup = NO_ACCEPTED_BIDS,
}: UseBidOfferEnrichedPtusParams): UseBidOfferEnrichedPtusResult {
  const enrichedPtusByActivationGroup = useMemo(() => {
    const map = new Map<string, EnrichedPtuData>()

    // Process each activation group
    for (const activationGroup of eligibleActivationGroups) {
      const activationGroupUuid = activationGroup.uuid

      // Step 1: Get bidBasis for this activation group
      const bidBasis = bidBasisByActivationGroup.get(activationGroupUuid)
      if (!bidBasis) {
        // If no bidBasis, skip this activation group
        continue
      }

      // Step 2: Create bidMetaData
      const bidMetaData = getBidMetaData(groupParams.deliveryDay, groupParams.bidType, activationGroup)

      // Step 3: Combine offered volumes with forecast and suggested bids
      const combinedPtus = combineOfferedAndForecast(bidBasis.ptus, bidBasis.forecast, bidBasis.suggestedBid)

      // Step 4: Get capacity bid history for this activation group
      const capacityBidHistory = capacityBids.hasCapacityBidOnThisDay(activationGroupUuid)
      const capacityBidHistories = capacityBidHistory ? [capacityBidHistory] : []

      // Step 5: Get bid history for this activation group (contains all bid types)
      const bidHistory = bidHistories.find((history) => history.activationGroupUuid === activationGroupUuid)
      const bidHistoriesForGroup = bidHistory ? [bidHistory] : []

      // Step 6: Generate initial PTUs with market-specific prefill logic
      // - Draft bids: All markets (checked in bidHistories)
      // - Capacity bids: mFRR Nordic ENERGY only (as fallback, checked in capacityBidHistories)
      // - Basis: Final fallback for all
      const initialPtus = generateInitialBidPtus({
        bidMetaData: bidMetaData,
        capacityBidHistories: capacityBidHistories,
        bidHistories: bidHistoriesForGroup,
        ptus: combinedPtus,
        latestNonDraftBids: latestNonDraftBidsByActivationGroup,
        acceptedBids: acceptedBidsByActivationGroup,
      })

      // Store enriched data in map
      map.set(activationGroupUuid, {
        bidMetaData: bidMetaData,
        ptus: combinedPtus,
        initialPtus: initialPtus,
      })
    }

    return map
  }, [
    groupParams,
    eligibleActivationGroups,
    bidBasisByActivationGroup,
    latestNonDraftBidsByActivationGroup,
    capacityBids,
    bidHistories,
    acceptedBidsByActivationGroup,
  ])

  // Memoize the return object to prevent unnecessary recalculations in dependent hooks
  return useMemo(
    () => ({
      enrichedPtusByActivationGroup,
    }),
    [enrichedPtusByActivationGroup],
  )
}
